import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface EvmPortfolioKpis {
  avgSpi: number | null;
  avgCpi: number | null;
  totalEac: number;
  totalBac: number;
  contractCount: number;
}

/**
 * Hook to fetch portfolio-level EVM KPIs for the dashboard.
 * Only returns data when epc_qtd_enabled flag is ON and EVM data exists.
 * Fully safe: returns null on any failure (no UI impact).
 */
export function useEvmPortfolioKpis(activeClusterIds: string[]) {
  return useQuery<EvmPortfolioKpis | null>({
    queryKey: ['evm-portfolio-kpis', activeClusterIds],
    queryFn: async () => {
      try {
        // 1. Check flag
        const { data: flag } = await (supabase as any)
          .from('emp_motor_flags')
          .select('ativo')
          .eq('flag_nome', 'epc_qtd_enabled')
          .maybeSingle();

        if (!flag?.ativo) {
          console.debug('[EVM:DASH] EVM_FLAG_DISABLED');
          return null;
        }

        // 2. Fetch latest EVM data per contract (most recent date)
        const { data: evmData, error } = await (supabase as any)
          .from('emp_vw_evm_contrato')
          .select('contrato_id, bac, ev, ac, spi, cpi, eac, data');

        if (error || !evmData || evmData.length === 0) {
          console.debug('[EVM:DASH] EVM_DATA_EMPTY');
          return null;
        }

        // Get latest record per contract
        const latestByContract = new Map<string, typeof evmData[0]>();
        for (const row of evmData) {
          const existing = latestByContract.get(row.contrato_id);
          if (!existing || row.data > existing.data) {
            latestByContract.set(row.contrato_id, row);
          }
        }

        // Filter to contracts in active clusters (via emp_contratos join)
        // Since the view already filters by tipo_contrato_operacional,
        // we just need cluster filtering
        const contractIds = Array.from(latestByContract.keys());
        if (contractIds.length === 0) return null;

        const { data: contracts } = await supabase
          .from('emp_contratos')
          .select('id, cluster_id')
          .in('id', contractIds)
          .in('cluster_id', activeClusterIds);

        const validIds = new Set((contracts || []).map(c => c.id));
        const filtered = Array.from(latestByContract.values()).filter(r => validIds.has(r.contrato_id));

        if (filtered.length === 0) return null;

        // Weighted average by BAC
        const totalBac = filtered.reduce((s, r) => s + (r.bac || 0), 0);
        const avgSpi = totalBac > 0
          ? filtered.reduce((s, r) => s + (r.spi || 0) * (r.bac || 0), 0) / totalBac
          : null;
        const avgCpi = totalBac > 0
          ? filtered.reduce((s, r) => s + (r.cpi || 0) * (r.bac || 0), 0) / totalBac
          : null;
        const totalEac = filtered.reduce((s, r) => s + (r.eac || 0), 0);

        console.debug('[EVM:DASH] Portfolio KPIs computed', {
          contractCount: filtered.length,
          totalBac,
          avgSpi,
          avgCpi,
          totalEac,
        });

        return {
          avgSpi: avgSpi !== null ? Math.round(avgSpi * 100) / 100 : null,
          avgCpi: avgCpi !== null ? Math.round(avgCpi * 100) / 100 : null,
          totalEac,
          totalBac,
          contractCount: filtered.length,
        };
      } catch (err) {
        console.warn('[EVM:DASH] Portfolio KPIs exception:', err);
        return null;
      }
    },
    enabled: activeClusterIds.length > 0,
    staleTime: 3 * 60 * 1000, // 3 min cache (aligned with EVM staleTime)
  });
}
